import React, { useState } from "react";
import { Modal, btnGhost, btnPrimary } from "./shared";

const SURFACE = { catalog: "Catalog", member_areas: "Member Areas", promo: "Promo Pack", platform_matrix: "Platform Matrix" };
const show = (v) => (typeof v === "object" && v !== null ? JSON.stringify(v, null, 1) : String(v ?? "") || "(empty)");

/**
 * Shown when the same field was changed elsewhere since this editor loaded it.
 * "Stored" = the other surface's version (by field_sources); "Mine" = the version from the current surface.
 */
export default function ConflictDialog({ conflicts, surface, onResolve, onCancel }) {
  const mineLabel = SURFACE[surface] || "This editor";
  const [choices, setChoices] = useState({});
  const labels = (c) => {
    const other = SURFACE[c.server_source] || "another session";
    const same = other === mineLabel;
    return { other: same ? `${other} (other session)` : other, mine: mineLabel };
  };
  const setAll = (choice) => setChoices(Object.fromEntries(conflicts.map((c) => [c.field, choice])));
  const complete = conflicts.every((c) => choices[c.field]);

  return (
    <Modal title="Sync conflict" onClose={onCancel} wide
      footer={<>
        <button type="button" className={btnGhost} onClick={onCancel}>Decide later</button>
        <button type="button" className={btnPrimary} disabled={!complete} onClick={() => onResolve(choices)}>Apply</button>
      </>}>
      <p className="text-sm text-zinc-300">The same field was edited in two places. Choose which version to keep.</p>
      <div className="flex flex-wrap gap-2">
        <button type="button" className={btnGhost} onClick={() => setAll("theirs")}>Keep stored versions</button>
        <button type="button" className={btnGhost} onClick={() => setAll("mine")}>Keep my versions</button>
      </div>
      {conflicts.map((c) => {
        const l = labels(c);
        return (
          <div key={c.field} className="rounded-lg border border-zinc-800 p-3 space-y-2" data-testid={`conflict-${c.field}`}>
            <div className="text-xs font-bold uppercase tracking-wider text-purple-300">{c.field.replace("platform_status.", "Platform · ").replace(/_/g, " ")}</div>
            <div className="grid sm:grid-cols-2 gap-2">
              {[["theirs", l.other, c.server_value], ["mine", l.mine, c.your_value]].map(([key, who, val]) => (
                <button key={key} type="button" onClick={() => setChoices((s) => ({ ...s, [c.field]: key }))}
                  aria-pressed={choices[c.field] === key}
                  className={`text-left rounded-lg border p-2.5 text-xs transition ${choices[c.field] === key ? "border-purple-400 bg-purple-900/20" : "border-zinc-700 hover:border-zinc-500"}`}>
                  <div className="font-semibold text-white mb-1">Keep {who} version</div>
                  <pre className="whitespace-pre-wrap break-words text-zinc-400 max-h-32 overflow-auto font-sans">{show(val)}</pre>
                </button>
              ))}
            </div>
          </div>
        );
      })}
    </Modal>
  );
}
